import { Component, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';

import moment, { Moment } from 'moment';
import { AppointmentResponse, IAppointment } from "../../models/appointment.interface";
import { TimeDifferencePipe } from "../../shared/pipes/time-difference.pipe";
import { NotificationClassEnum } from '../../models/notification-class.enum';
import { AppointmentService } from "../../services/appointment/appointment.service";
import { CVANotificationService } from '../../services/cva-notification/cva-notification.service';
import { ActionClick, GridColumn } from '../../models/data-table.interface';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HistoricalDataModalData } from '../../models/historical-data.interface';
import { environment } from '../../../environments/environment';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { FormControl, FormGroup } from '@angular/forms';
import { AppointmentStatusEnum, CVA_CONSTANTS, MANUAL_REFRESH_IDENTIFIER } from '../../util/constants';
import { AdditionalData, ManualResponse } from '../../models/manual-refresh.interface';
import { SoapServiceService } from "../../services/soap-service/soap-service.service";
import { ConfirmationDialogService } from '../../shared/confirmation-dialog/services/confirmation-dialog.service';
import { lastValueFrom } from 'rxjs';

@Component({
    standalone: false,
    selector: 'cva-patient-appointment-list',
    templateUrl: './patient-appointment-list.component.html',
    styleUrl: './patient-appointment-list.component.scss',
    
})
export class PatientAppointmentListComponent implements OnInit, OnChanges {
  @Input() patientIdInput: string = '';
  @ViewChild(MatDateRangePicker) picker!: MatDateRangePicker<Moment>;

  patientId: string;
  appointments!: IAppointment[];

  bookAppointmentMode: boolean = false;
  isPrevArrowDisabled: boolean = true;

  deleteConfirmationMessage: string = CVA_CONSTANTS.MESSAGE.DELETE_CONFIRMATION_MESSAGE;
  cvaUI_url: string = environment.cvaUI_url;

  currentDate: Moment = moment();
  start: Moment = moment().startOf('day');
  end: Moment = moment().endOf('day');
  displayDate: string = '';

  range = new FormGroup({
    startDate: new FormControl<Date | null>(null),
    endDate: new FormControl<Date | null>(null),
  });

  displayedColumns: GridColumn<IAppointment>[] = [
    { field: "time", label: "Time", tdClass: "apt-time" },
    { field: "name", label: "Provider Name", width: "15%", tdClass: "practitioner-name" },
    { field: "appointmentType", label: "Visit Type", tdClass: "normal-text" },
    { field: "notes", label: "Reason", width: "25%", tdClass: "notes-field normal-text text-break", hasLargeText: true },
    { field: "status", label: "Status", tdClass: "status-field normal-text" },
    { field: "duration", label: "Duration", tdClass: "normal-text" }
  ];
  manualRefreshIdentifier: MANUAL_REFRESH_IDENTIFIER =
    MANUAL_REFRESH_IDENTIFIER.PATIENT_APPOINTMENT;
  additionalParams!: AdditionalData;
  readonly isDialogMode: boolean;

  constructor(
    private readonly apiService: AppointmentService,
    private readonly cvaNotificationService: CVANotificationService,
    private readonly timeDifferencePipe: TimeDifferencePipe,
    private readonly datePipe: DatePipe,
    private readonly soapServiceService: SoapServiceService,
    private readonly confirmationDialogService: ConfirmationDialogService,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly modalData: HistoricalDataModalData | null,
  ) {
    this.patientId = modalData?.patientId ?? '';
    this.isDialogMode = !!modalData;
  }

  ngOnInit() {
    this.syncPatientIdFromInput();
    this.setFormattedDateRange();
    if (this.patientId?.trim()) {
      this.fetchAppointments();
      this.setManualRefreshAdditionalParams(this.start, this.end);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['patientIdInput'] && !changes['patientIdInput'].firstChange) {
      this.syncPatientIdFromInput();
      if (this.patientId?.trim()) {
        this.fetchAppointments();
      }
    }
  }

  private syncPatientIdFromInput(): void {
    if (this.patientIdInput?.trim()) {
      this.patientId = this.patientIdInput.trim();
    }
  }


  goToPrev(type: 'week' | 'month') {
    this.start = this.start.clone().subtract(1, 'day').startOf(type);
    this.end = this.start.clone().endOf(type)

    this.isPrevArrowDisabled = moment(this.currentDate).isBetween(this.start, this.end);
    this.setFormattedDateRange(type === 'month');
    this.patchDateRange();
    this.fetchAppointments();
  }

  goToNext(type: 'week' | 'month') {
    const isSameDate = this.start.isSame(this.end, 'date');
    this.start = isSameDate ?
      this.end.clone().startOf(type) :
      this.end.clone().add(1, 'day').startOf(type);

    this.end = this.start.clone().endOf(type);
    this.isPrevArrowDisabled = moment(this.currentDate).isBetween(this.start, this.end);

    this.setFormattedDateRange(type === 'month');
    this.patchDateRange();
    this.fetchAppointments()
  }

  onDateSelect() {
    const { startDate, endDate } = this.range.value;
    if (startDate && endDate) {
      this.start = moment(startDate).startOf('day')
      this.end = moment(endDate).endOf('day')
      this.setFormattedDateRange();
      this.fetchAppointments();
    }
  }

  /**
   * on datepicker close
   * update the end date when user click once to select start date
   * and click outside the calendar
   */
  onDatepickerClosed() {
    const { startDate, endDate } = this.range.value;
    if (startDate && !endDate) {
      this.range.patchValue({ endDate: startDate });
      this.onDateSelect();
    }
  }

  patchDateRange() {
    this.range.patchValue({
      startDate: this.start.toDate(),
      endDate: this.end.toDate(),
    })
  }

  setFormattedDateRange(isMonthly: boolean = false): void {
    if (!this.start || !this.end) return;

    const start = moment(this.start);
    const end = moment(this.end);
    const today = moment();

    if (isMonthly) {
      this.displayDate = start.format('MMM, YYYY');
      return;
    }

    if (start.isSame(end, 'date')) {
      this.setSingleDayDisplay(start, today);
      return;
    }

    this.setMultiDayDisplay(start, end);
  }

  private setSingleDayDisplay(start: Moment, today: Moment): void {
    if (today.isBetween(start, start, undefined, '[]')) {
      this.displayDate = `Today ${start.format('DD MMM')}`;
    } else {
      this.displayDate = start.format('DD MMM, YYYY');
    }
  }

  private setMultiDayDisplay(start: Moment, end: Moment): void {
    const sameYear = start.year() === end.year();
    const sameMonth = start.month() === end.month();

    if (sameYear && sameMonth) {
      this.displayDate = `${start.format('DD')}-${end.format('DD MMM, YYYY')}`;
    } else if (sameYear) {
      this.displayDate = `${start.format('DD MMM')}-${end.format('DD MMM, YYYY')}`;
    } else {
      this.displayDate = `${start.format('DD MMM, YYYY')}-${end.format('DD MMM, YYYY')}`;
    }
  }

  onActionClick(record: ActionClick<IAppointment>): void {
    if (record.action === "delete") {
      this.cancelAppointment(
        record.rowData.uid,
        record.rowData.start,
        record.rowData.referenceId
      );
    }
  }

  filterAppointment(appointments: IAppointment[]) {
    return appointments.sort((a, b) => a.start.localeCompare(b.start));
  }

  setAppointmentRecords(records: AppointmentResponse[]) {
    const appointments: IAppointment[] = records.map((appointment) => {
      return {
        ...appointment,
        time: this.datePipe.transform(appointment.start, 'd MMM, y HH:mm') ?? '',
        name: `${appointment.practitioner?.firstName} ${appointment.practitioner?.lastName}`,
        duration: this.timeDifferencePipe.transform(appointment.start, appointment.end),
        isDeleted: appointment.status === AppointmentStatusEnum.CANCELLED,
        hasTranscription: (appointment.transcription && appointment.transcription.length > 0) || false
      };
    });
    return this.filterAppointment(appointments);
  }

  fetchAppointments() {
    let start = this.start.clone();

    if (this.start < moment()) {
      start = moment().startOf('D');
    }
    if (this.start > this.end) {
      this.end = start.clone().endOf('day');
    }
    this.setManualRefreshAdditionalParams(start, this.end);

    this.apiService.getAppointmentsByPatientId(
      this.patientId,
      start.toDate(),
      this.end.toDate(),
      "false"
    ).subscribe({
      next: (res: { response: AppointmentResponse[] }) => {
        this.appointments = this.setAppointmentRecords(res.response);
        console.log('Appointments fetched successfully.')
      },
      error: (errorResponse: HttpErrorResponse) => {
        let errorMessage: string = '';
        if (errorResponse.error?.error) {
          errorMessage = errorResponse.error.error.errorMessage;
        }
        this.cvaNotificationService.showNotification(
          errorMessage || CVA_CONSTANTS.MESSAGE.NO_APPOINTMENT,
          NotificationClassEnum.ERROR
        );
        console.error(errorMessage)
      }
    });
  }

  cancelAppointment(uid: string, start: string, referenceId: string) {
    const confirmed$ = this.confirmationDialogService.openConfirmDialog(CVA_CONSTANTS.MESSAGE.DELETE_CONFIRMATION_MESSAGE);
    lastValueFrom(confirmed$).then((isConfirmed: boolean) => {
      if (isConfirmed === true) {
        if (moment().isAfter(moment(start))) {
          this.cvaNotificationService.showNotification(
            CVA_CONSTANTS.MESSAGE.UNABLE_TO_CANCEL_THE_PAST_APPOINTMENT,
            NotificationClassEnum.ERROR
          );
          return;
        }

        //verify the saved appointment is same
        const savedConsultation = this.soapServiceService.getAppointmentAndDraftedNotes();
        if (savedConsultation?.referenceId === referenceId) {
          this.cvaNotificationService.showNotification(
            CVA_CONSTANTS.MESSAGE.UNABLE_TO_CANCEL_ONGOING_APPOINTMENT,
            NotificationClassEnum.ERROR
          );
          return;
        }

        //make api call
        this.apiService.cancelAppointmentById(uid).subscribe({
          next: () => {
            const appointment = this.appointments.find(appointment => appointment.uid === uid);
            if (appointment) {
              appointment.isDeleted = true;
              appointment.status = AppointmentStatusEnum.CANCELLED;
            }
            this.appointments = JSON.parse(JSON.stringify(this.appointments))

            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.APPOINTMENT_CANCELLED_SUCCESSFULLY,
              NotificationClassEnum.SUCCESS
            );
            console.log(CVA_CONSTANTS.MESSAGE.APPOINTMENT_CANCELLED_SUCCESSFULLY)
          },
          error: (errorResponse: HttpErrorResponse) => {
            let errorMessage: string = '';
            if (errorResponse.error?.error) {
              errorMessage = errorResponse.error.error.errorMessage;
              if (errorMessage === CVA_CONSTANTS.MESSAGE.NO_RECORD_FOUND) {
                errorMessage = CVA_CONSTANTS.MESSAGE.UNABLE_TO_CANCEL_THE_APPOINTMENT
              }
            }
            this.cvaNotificationService.showNotification(
              errorMessage || CVA_CONSTANTS.MESSAGE.UNABLE_TO_CANCEL_THE_APPOINTMENT,
              NotificationClassEnum.ERROR
            );
            console.error(errorMessage)
          }
        });
      }
    });


  }

  patchRefreshedData(data: ManualResponse<unknown>): void {
    if (data.identifier === this.manualRefreshIdentifier) {
      this.appointments = this.setAppointmentRecords(data.records as AppointmentResponse[]);
    }
  }

  setManualRefreshAdditionalParams(start: Moment, end: Moment) {
    this.additionalParams = {
      start: moment(start).startOf('day').toISOString(),
      end: moment(end).endOf('day').toISOString(),
    } as AdditionalData;
  }
}
